// trunk_eye -> code, geom_eye -> geom_uv, appr_eye -> color_uv. Shared by both eyes.

import * as realOrt from 'onnxruntime-web'
import { withOrtGate } from '../../lib/ortGate.js'
import { ensureOgsOrtEnv } from '../graph/decoderBuild.ts'

export type EyeDecoderMaps = { geomUv: Float32Array, colorUv: Float32Array, colorChannels: number }

export type EyeDecoders = {
  run(cond: Float32Array, plucker: Float32Array): Promise<EyeDecoderMaps>
  provider: string
  uvResolution: number
  dispose(): Promise<void>
}

// A bundle hands over model BYTES; the test scene hands over URLs. ORT takes either.
export type EyeModelSource = string | Uint8Array
export type EyeDecoderUrls = { trunk: EyeModelSource, geom: EyeModelSource, appr: EyeModelSource }

export type EyeDecoderOptions = {
  preferred?: string[]
  // Test seam: proving no ORT op ever overlaps another needs an injectable runtime.
  ort?: any
}

export async function createEyeDecoders(urls: EyeDecoderUrls, uvResolution: number,
                                       options: EyeDecoderOptions = {}): Promise<EyeDecoders> {
  const ort = options.ort ?? realOrt
  const preferred = options.preferred ?? ['webgpu', 'wasm']
  let sessions: any = null
  let chosenProvider = ''
  let lastError: unknown = null
  for (const provider of preferred) {
    try {
      const sessionOptions = { executionProviders: [provider], graphOptimizationLevel: 'all' as const }
      // Sequential inside ONE gate acquisition, and ensureOgsOrtEnv re-asserted per create.
      const [trunk, geom, appr] = await withOrtGate(async () => {
        ensureOgsOrtEnv(ort as never)
        return [
          await ort.InferenceSession.create(urls.trunk, sessionOptions),
          await ort.InferenceSession.create(urls.geom, sessionOptions),
          await ort.InferenceSession.create(urls.appr, sessionOptions),
        ]
      })
      sessions = { trunk, geom, appr }
      chosenProvider = provider
      break
    } catch (error) {
      // A provider that cannot build is worth reporting: it is 30ms vs 2s per pass.
      console.warn(`[eyes] execution provider '${provider}' unavailable:`,
        (error as any)?.message ?? error)
      lastError = error
    }
  }
  if (!sessions) throw new Error(`eye decoders: no usable execution provider (${lastError})`)
  console.info(`[eyes] decoders ready on '${chosenProvider}' (uv ${uvResolution}²)`)

  // Names come from the export; a renamed input would silently feed zeros.
  const trunkInput = sessions.trunk.inputNames[0]
  const geomInput = sessions.geom.inputNames[0]
  const apprInputs = sessions.appr.inputNames

  // Per RUN through the SHARED gate — the JSEP runtime runs one inference process-wide.
  const gated = <T>(operation: () => Promise<T>): Promise<T> => withOrtGate(operation)

  async function run(cond: Float32Array, plucker: Float32Array): Promise<EyeDecoderMaps> {
    if (cond.length !== 12) throw new Error(`eye decoders: cond must be 12, got ${cond.length}`)
    const expected = 6 * uvResolution * uvResolution
    if (plucker.length !== expected) {
      throw new Error(`eye decoders: plucker must be 6x${uvResolution}x${uvResolution} (${expected}), got ${plucker.length}`)
    }
    const condTensor = new ort.Tensor('float32', cond, [1, 12])
    const trunkOutput = await gated(() => sessions.trunk.run({ [trunkInput]: condTensor }))
    const code = trunkOutput[sessions.trunk.outputNames[0]]

    const pluckerTensor = new ort.Tensor('float32', plucker, [1, 6, 1, uvResolution, uvResolution])
    const apprFeeds: Record<string, any> = {}
    for (const name of apprInputs) {
      apprFeeds[name] = name === trunkInput || name.includes('code') ? code : pluckerTensor
    }
    const geomOutput = await gated(() => sessions.geom.run({ [geomInput]: code }))
    const apprOutput = await gated(() => sessions.appr.run(apprFeeds))
    const geomUv = geomOutput[sessions.geom.outputNames[0]]
    const colorUv = apprOutput[sessions.appr.outputNames[0]]

    const [, geomChannels, geomHeight, geomWidth] = geomUv.dims
    if (geomHeight !== uvResolution || geomWidth !== uvResolution) {
      throw new Error(`eye decoders: geom_uv is ${geomHeight}x${geomWidth}, chart is ${uvResolution}² — `
        + `the maps must be emitted at the CHART resolution`)
    }
    if (geomChannels !== 11) {
      throw new Error(`eye decoders: geom_uv has ${geomChannels} channels, expected 11`)
    }
    return {
      geomUv: geomUv.data as Float32Array,
      colorUv: colorUv.data as Float32Array,
      colorChannels: colorUv.dims[1],
    }
  }

  return {
    run,
    provider: chosenProvider,
    uvResolution,
    async dispose() {
      for (const session of Object.values(sessions) as any[]) await session.release?.()
    },
  }
}
